import React from 'react';
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, isWeekend, startOfMonth, startOfWeek } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { Holiday } from '../../types/selfService';
import { holidayMap } from '../../utils/holidays';
interface RangeCalendarProps {
  month: Date;
  onMonthChange: (month: Date) => void;
  start?: string;
  end?: string;
  onSelect: (iso: string) => void;
  holidays: Holiday[];
  minIso: string;
}
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
export function RangeCalendar({
  month,
  onMonthChange,
  start,
  end,
  onSelect,
  holidays,
  minIso
}: RangeCalendarProps) {
  const map = holidayMap(holidays);
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month)),
    end: endOfWeek(endOfMonth(month))
  });
  const rangeEnd = end ?? start;
  const canGoBack = format(month, 'yyyy-MM') > minIso.slice(0, 7);
  return <div data-id="element-794" data-mp="1xd27b4y0htpz">
      <div className="flex items-center justify-between" data-id="element-795" data-mp="1xdbxasj240vo">
        <button type="button" onClick={() => onMonthChange(addMonths(month, -1))} disabled={!canGoBack} aria-label="Previous month" className="flex h-9 w-9 items-center justify-center rounded-xl text-navy ring-1 ring-inset ring-slate-200 transition hover:bg-canvas disabled:opacity-30" data-id="element-796" data-mp="1zd9sdqjl4gr4">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-797" data-mp="2i2ggjt35e0kb" />
        </button>
        <p className="text-sm font-semibold text-navy" aria-live="polite" data-id="element-798" data-mp="2i26ps3aa3pdk">
          {format(month, 'MMMM yyyy')}
        </p>
        <button type="button" onClick={() => onMonthChange(addMonths(month, 1))} aria-label="Next month" className="flex h-9 w-9 items-center justify-center rounded-xl text-navy ring-1 ring-inset ring-slate-200 transition hover:bg-canvas" data-id="element-799" data-mp="2n3chbxs7d6li">
          <ChevronRightIcon className="h-4 w-4" data-id="element-800" data-mp="xoo5pyrfrsoj" />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-7 gap-y-1 text-center" data-id="element-801" data-mp="12m7uvtpo9pv9">
        {WEEKDAYS.map(d => <span key={d} className="pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-802" data-mp="ulibvnh3b40y">
            {d}
          </span>)}
        {days.map(d => {
        const iso = format(d, 'yyyy-MM-dd');
        if (!isSameMonth(d, month)) return <span key={iso} aria-hidden="true" data-id="element-803" data-mp="zjjuk7alt1bw" />;
        const weekend = isWeekend(d);
        const h = map.get(iso);
        const fullHoliday = Boolean(h && !h.halfDay);
        const past = iso < minIso;
        const disabled = weekend || fullHoliday || past;
        const isStart = iso === start;
        const isEnd = iso === rangeEnd;
        const inRange = Boolean(start && rangeEnd && iso > start && iso < rangeEnd);
        let cell = 'text-slate-700 hover:bg-azure/10';
        if (disabled) cell = 'cursor-not-allowed text-slate-300';
        if (fullHoliday && !past) cell = 'cursor-not-allowed bg-flame/[0.08] text-flame/60 line-through decoration-flame/40';
        if (inRange) cell = disabled ? 'bg-azure/[0.06] text-slate-300' : 'bg-azure/15 text-navy';
        if (isStart || isEnd) cell = 'bg-navy font-semibold text-white';
        const roundL = isStart || !inRange ? 'rounded-l-xl' : '';
        const roundR = isEnd || !inRange ? 'rounded-r-xl' : '';
        return <button key={iso} type="button" disabled={disabled} onClick={() => onSelect(iso)} aria-pressed={isStart || isEnd || inRange} aria-label={`${format(d, 'EEEE, MMMM d')}${h ? `, ${h.name}${h.halfDay ? ' (half day)' : ''}` : ''}${weekend ? ', weekend' : ''}`} title={h ? `${h.name}${h.halfDay ? ' · half day' : ' · office closed'}` : weekend ? 'Weekend — not counted' : undefined} className={`relative mx-auto flex h-10 w-full items-center justify-center text-sm transition ${roundL} ${roundR} ${cell}`} data-id="element-804" data-mp="26twbjfatxp0p">
              {format(d, 'd')}
              {h?.halfDay && <span className={`absolute bottom-1 h-1 w-3 rounded-full ${isStart || isEnd ? 'bg-flame' : 'bg-flame/70'}`} aria-hidden="true" data-id="element-805" data-mp="2gj53ffrq86db" />}
            </button>;
      })}
      </div>

      <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-muted" data-id="element-806" data-mp="27qustxcx5yvj">
        <li className="flex items-center gap-1.5" data-id="element-807" data-mp="22tl5qod05zav">
          <span className="h-3 w-3 rounded bg-navy" aria-hidden="true" data-id="element-808" data-mp="22qp8l28zbenh" /> Selected
        </li>
        <li className="flex items-center gap-1.5" data-id="element-809" data-mp="1xv018cqpwtgm">
          <span className="h-3 w-3 rounded bg-flame/20" aria-hidden="true" data-id="element-810" data-mp="1sucew4dfp0sq" /> Company holiday
        </li>
        <li className="flex items-center gap-1.5" data-id="element-811" data-mp="1os8s4jlbe9s5">
          <span className="h-1 w-3 rounded-full bg-flame/70" aria-hidden="true" data-id="element-812" data-mp="1ouv8n8q6nzmi" /> Half-day holiday
        </li>
        <li className="flex items-center gap-1.5" data-id="element-813" data-mp="3j71j25b96cdr">
          <span className="text-slate-300" data-id="element-814" data-mp="3j5qieed3tkqv">12</span> Weekend · not counted
        </li>
      </ul>
    </div>;
}